import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { getNav } from "@/lib/navConfig";
import api, { money } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { RequestNumber, StatusTimeline } from "@/components/RequestTracker";
import { SyncStatusBar } from "@/pages/contractor/FieldPanel";
import { InstallPrompt } from "@/pages/contractor/InstallPrompt";
import { Briefcase, Wrench, Calendar, Loader2, DollarSign, CheckCircle2, Clock, MapPin } from "lucide-react";

export function ContractorDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/dashboard/contractor").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/work-orders").then(({ data }) => setJobs(data)).catch(() => {});
  }, []);

  return (
    <DashboardLayout nav={getNav("contractor")} title="My Jobs" bottomNav>
      <SyncStatusBar />
      <InstallPrompt />
      {!stats ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="space-y-6">
          {stats.application_status !== "Active" && stats.application_status !== "Approved" && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
              Your application status is <strong>{stats.application_status}</strong>. Complete your profile & documents to get activated for job assignments.
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard testid="c-available" label="Available" value={stats.available_jobs} icon={Wrench} />
            <StatCard testid="c-assigned" label="Assigned" value={stats.assigned_jobs} icon={Briefcase} accent="text-blue-600" />
            <StatCard testid="c-scheduled" label="Scheduled" value={stats.scheduled_jobs} icon={Calendar} accent="text-purple-600" />
            <StatCard testid="c-inprogress" label="In Progress" value={stats.jobs_in_progress} icon={Clock} accent="text-indigo-600" />
            <StatCard testid="c-completed" label="Completed" value={stats.completed_jobs} icon={CheckCircle2} accent="text-emerald-600" />
            <StatCard testid="c-pending-pay" label="Pending Pay" value={money(stats.pending_payment)} icon={DollarSign} accent="text-amber-600" />
            <StatCard testid="c-paid" label="Paid Earnings" value={money(stats.paid_earnings)} icon={DollarSign} accent="text-green-600" />
          </div>

          <h2 className="font-head text-lg font-semibold text-slate-900">Assigned to Me</h2>
          {jobs.length === 0 ? <p className="text-slate-500 text-sm">No assigned jobs. Check available jobs.</p> : (
            <div className="space-y-2">
              {jobs.map((w) => (
                <button key={w.id} data-testid={`job-row-${w.id}`} onClick={() => navigate(`/app/wo/${w.id}`)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <RequestNumber number={w.request_number || w.wo_number} urgency={w.urgency} />
                    <StatusBadge status={w.status} />
                  </div>
                  <div className="mt-3"><StatusTimeline wo={w} /></div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    <UrgencyBadge urgency={w.urgency} /> {w.service_category}
                  </div>
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {w.property?.city}, {w.property?.state}</div>
                  {w.contractor_payout != null && <div className="mt-2 text-sm font-semibold text-green-600">Payout: {money(w.contractor_payout)}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}

export function ContractorAvailable() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  useEffect(() => { api.get("/work-orders/available").then(({ data }) => setJobs(data)).catch(() => setJobs([])); }, []);

  return (
    <DashboardLayout nav={getNav("contractor")} title="Available Jobs" bottomNav>
      {!jobs ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        jobs.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-slate-200"><Wrench className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No available jobs right now.</p></div> : (
          <div className="space-y-2">
            {jobs.map((w) => (
              <button key={w.id} data-testid={`available-job-${w.id}`} onClick={() => navigate(`/app/wo/${w.id}`)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-center justify-between"><span className="font-medium text-slate-900">{w.wo_number}</span><UrgencyBadge urgency={w.urgency} /></div>
                <div className="text-sm text-slate-600 mt-1">{w.service_category} — {w.problem_description}</div>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {w.property?.city}, {w.property?.state}</div>
              </button>
            ))}
          </div>
        )}
    </DashboardLayout>
  );
}
